/**
 * Investor Center - NWGSV Command System
 * Phase C3: Capital Access, Deal Flow, Pitch Decks, Performance Analytics
 * RBAC: SUPER_ADMIN and INVESTOR only
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../utils/api';
import './investor.css';

// Types
interface DashboardData {
  portfolio_summary: {
    total_invested: number;
    current_value: number;
    roi_percentage: number;
    active_investments: number;
  };
  investment_requests: {
    total: number;
    pending: number;
  };
  deal_pipeline: {
    total_deals: number;
    active_deals: number;
  };
  pitch_decks: number;
  recent_activity: any[];
}

interface InvestmentRequest {
  id: string;
  title: string;
  description: string;
  amount_requested: number;
  purpose: string;
  status: string;
  submitted_by: string;
  submitted_at: string;
  reviewed_by?: string;
  reviewed_at?: string;
  notes?: string;
}

interface PortfolioInvestment {
  id: string;
  investment_name: string;
  amount_invested: number;
  investment_date: string;
  current_value: number;
  roi_percentage: number;
  status: string;
}

interface Deal {
  id: string;
  deal_name: string;
  company: string;
  amount: number;
  stage: string;
  probability: number;
  expected_close_date?: string;
  created_at: string;
}

interface PitchDeck {
  id: string;
  filename: string;
  title: string;
  description: string;
  uploaded_by: string;
  uploaded_at: string;
  version: number;
}

interface Analytics {
  portfolio_performance: {
    total_invested: number;
    total_current_value: number;
    total_roi: number;
    active_investments: number;
    exited_investments: number;
  };
  deal_conversion: {
    closed_won: number;
    closed_lost: number;
    active_deals: number;
    win_rate: number;
  };
  top_investments: PortfolioInvestment[];
}

export default function InvestorCenter({ onBack }: { onBack: () => void }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'requests' | 'portfolio' | 'deals' | 'pitch-decks' | 'analytics'>('dashboard');
  
  // Data states
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [requests, setRequests] = useState<InvestmentRequest[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioInvestment[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [pitchDecks, setPitchDecks] = useState<PitchDeck[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === 'requests') loadRequests();
    else if (activeTab === 'portfolio') loadPortfolio();
    else if (activeTab === 'deals') loadDeals();
    else if (activeTab === 'pitch-decks') loadPitchDecks();
    else if (activeTab === 'analytics') loadAnalytics();
  }, [activeTab]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/investor/dashboard');
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    try {
      const response = await apiCall('/api/investor/requests');
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const loadPortfolio = async () => {
    try {
      const response = await apiCall('/api/investor/portfolio');
      if (response.ok) {
        const data = await response.json();
        setPortfolio(data);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const loadDeals = async () => {
    try {
      const response = await apiCall('/api/investor/deals');
      if (response.ok) {
        const data = await response.json();
        setDeals(data);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const loadPitchDecks = async () => {
    try {
      const response = await apiCall('/api/investor/pitch-decks');
      if (response.ok) {
        const data = await response.json();
        setPitchDecks(data);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await apiCall('/api/investor/analytics');
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCreateRequest = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('/api/investor/requests', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (response.ok) {
        setShowForm(false);
        await loadRequests();
        form.reset();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create request');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUploadPitchDeck = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    if (selectedFile) {
      formData.append('file', selectedFile);
    }
    
    try {
      const response = await fetch('/api/investor/pitch-decks', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (response.ok) {
        setShowForm(false);
        setSelectedFile(null);
        await loadPitchDecks();
        form.reset();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to upload pitch deck');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDownloadPitchDeck = async (deckId: string, filename: string) => {
    try {
      const response = await fetch(`/api/investor/pitch-decks/${deckId}/download`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'Pending': '#FFD700',
      'Under Review': '#FFA500',
      'Approved': '#00C853',
      'Funded': '#4CAF50',
      'Rejected': '#ff4444',
      'Active': '#00C853',
      'Exited': '#9E9E9E',
      'Closed Won': '#4CAF50',
      'Closed Lost': '#ff4444'
    };
    return colors[status] || '#FFD700';
  };

  return (
    <div className="module-root" style={styles.container}>
      {/* Header */}
      <div className="module-header" style={styles.header}>
        <div>
          <h1 style={styles.title}>INVESTOR CENTER</h1>
          <p style={styles.subtitle}>Capital Access • Deal Flow • Performance Analytics</p>
        </div>
        <button style={styles.backButton} onClick={onBack}>
          ← RETURN TO CUBE
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Tab Navigation */}
      <div className="module-tabbar" style={styles.tabBar}>
        {['dashboard', 'requests', 'portfolio', 'deals', 'pitch-decks', 'analytics'].map(tab => (
          <button
            key={tab}
            style={{
              ...styles.tab,
              ...(activeTab === tab ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab(tab as any)}
          >
            {tab.toUpperCase().replace(/-/g, ' ')}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="module-content" style={styles.content}>
        {/* DASHBOARD */}
        {activeTab === 'dashboard' && dashboard && (
          <div style={styles.dashboardGrid}>
            <div style={styles.card} onClick={() => setActiveTab('portfolio')}>
              <div style={styles.cardIcon}>💼</div>
              <div style={styles.cardNumber}>{formatCurrency(dashboard.portfolio_summary.total_invested)}</div>
              <div style={styles.cardLabel}>Total Invested</div>
              <div style={styles.cardMeta}>ROI: {dashboard.portfolio_summary.roi_percentage}%</div>
            </div>
            <div style={styles.card} onClick={() => setActiveTab('portfolio')}>
              <div style={styles.cardIcon}>📈</div>
              <div style={styles.cardNumber}>{formatCurrency(dashboard.portfolio_summary.current_value)}</div>
              <div style={styles.cardLabel}>Current Value</div>
              <div style={styles.cardMeta}>{dashboard.portfolio_summary.active_investments} Active</div>
            </div>
            <div style={styles.card} onClick={() => setActiveTab('requests')}>
              <div style={styles.cardIcon}>📋</div>
              <div style={styles.cardNumber}>{dashboard.investment_requests.total}</div>
              <div style={styles.cardLabel}>Investment Requests</div>
              <div style={styles.cardMeta}>{dashboard.investment_requests.pending} Pending</div>
            </div>
            <div style={styles.card} onClick={() => setActiveTab('deals')}>
              <div style={styles.cardIcon}>🎯</div>
              <div style={styles.cardNumber}>{dashboard.deal_pipeline.active_deals}</div>
              <div style={styles.cardLabel}>Active Deals</div>
              <div style={styles.cardMeta}>{dashboard.deal_pipeline.total_deals} Total</div>
            </div>
            <div style={styles.card} onClick={() => setActiveTab('pitch-decks')}>
              <div style={styles.cardIcon}>📊</div>
              <div style={styles.cardNumber}>{dashboard.pitch_decks}</div>
              <div style={styles.cardLabel}>Pitch Decks</div>
            </div>
            <div style={styles.card} onClick={() => setActiveTab('analytics')}>
              <div style={styles.cardIcon}>📉</div>
              <div style={styles.cardNumber}>Analytics</div>
              <div style={styles.cardLabel}>Performance Metrics</div>
            </div>
          </div>
        )}

        {/* INVESTMENT REQUESTS */}
        {activeTab === 'requests' && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Capital Requests</h2>
              <button style={styles.createButton} onClick={() => setShowForm(!showForm)}>
                {showForm ? 'CANCEL' : '+ NEW REQUEST'}
              </button>
            </div>

            {showForm && (
              <form onSubmit={handleCreateRequest} style={styles.form}>
                <input name="title" placeholder="Request Title" required style={styles.input} />
                <textarea name="description" placeholder="Description" required style={styles.textarea} rows={3} />
                <input name="amount_requested" type="number" placeholder="Amount Requested" required style={styles.input} />
                <input name="purpose" placeholder="Purpose" required style={styles.input} />
                <button type="submit" style={styles.submitButton}>SUBMIT REQUEST</button>
              </form>
            )}

            <div style={styles.requestsList}>
              {requests.map(req => (
                <div key={req.id} style={styles.requestCard}>
                  <div style={styles.requestHeader}>
                    <div style={styles.requestTitle}>{req.title}</div>
                    <div style={{...styles.requestStatus, color: getStatusColor(req.status)}}>
                      {req.status}
                    </div>
                  </div>
                  <div style={styles.requestDesc}>{req.description}</div>
                  <div style={styles.requestAmount}>Amount: {formatCurrency(req.amount_requested)}</div>
                  <div style={styles.requestPurpose}>Purpose: {req.purpose}</div>
                  <div style={styles.requestDate}>
                    Submitted: {new Date(req.submitted_at).toLocaleDateString()}
                  </div>
                  {req.notes && <div style={styles.requestNotes}>Notes: {req.notes}</div>}
                </div>
              ))}
              {requests.length === 0 && (
                <div style={styles.empty}>No investment requests yet</div>
              )}
            </div>
          </div>
        )}

        {/* PORTFOLIO */}
        {activeTab === 'portfolio' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Investment Portfolio</h2>
            
            <div style={styles.portfolioGrid}>
              {portfolio.map(inv => (
                <div key={inv.id} style={styles.portfolioCard}>
                  <div style={styles.portfolioName}>{inv.investment_name}</div>
                  <div style={styles.portfolioAmount}>Invested: {formatCurrency(inv.amount_invested)}</div>
                  <div style={styles.portfolioValue}>Current: {formatCurrency(inv.current_value)}</div>
                  <div style={{
                    ...styles.portfolioRoi,
                    color: inv.roi_percentage >= 0 ? '#00C853' : '#ff4444'
                  }}>
                    ROI: {inv.roi_percentage > 0 ? '+' : ''}{inv.roi_percentage.toFixed(2)}%
                  </div>
                  <div style={{...styles.portfolioStatus, color: getStatusColor(inv.status)}}>
                    {inv.status}
                  </div>
                  <div style={styles.portfolioDate}>
                    {new Date(inv.investment_date).toLocaleDateString()}
                  </div>
                </div>
              ))}
              {portfolio.length === 0 && (
                <div style={styles.empty}>No portfolio investments yet</div>
              )}
            </div>
          </div>
        )}

        {/* DEAL PIPELINE */}
        {activeTab === 'deals' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Deal Pipeline</h2>
            
            <div style={styles.dealsList}>
              {deals.map(deal => (
                <div key={deal.id} style={styles.dealCard}>
                  <div style={styles.dealHeader}>
                    <div style={styles.dealName}>{deal.deal_name}</div>
                    <div style={styles.dealAmount}>{formatCurrency(deal.amount)}</div>
                  </div>
                  <div style={styles.dealCompany}>{deal.company}</div>
                  <div style={styles.dealStage}>Stage: {deal.stage}</div>
                  <div style={styles.dealProbability}>Probability: {deal.probability}%</div>
                  {deal.expected_close_date && (
                    <div style={styles.dealDate}>
                      Expected Close: {new Date(deal.expected_close_date).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))}
              {deals.length === 0 && (
                <div style={styles.empty}>No deals in pipeline</div>
              )}
            </div>
          </div>
        )}

        {/* PITCH DECKS */}
        {activeTab === 'pitch-decks' && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Pitch Decks</h2>
              <button style={styles.createButton} onClick={() => setShowForm(!showForm)}>
                {showForm ? 'CANCEL' : '+ UPLOAD'}
              </button>
            </div>

            {showForm && (
              <form onSubmit={handleUploadPitchDeck} style={styles.form}>
                <input name="title" placeholder="Pitch Deck Title" required style={styles.input} />
                <textarea name="description" placeholder="Description" required style={styles.textarea} rows={2} />
                <input
                  type="file"
                  accept=".pdf,.ppt,.pptx"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  required
                  style={styles.input}
                />
                <button type="submit" style={styles.submitButton} disabled={!selectedFile}>
                  UPLOAD PITCH DECK
                </button>
              </form>
            )}

            <div style={styles.pitchDecksList}>
              {pitchDecks.map(deck => (
                <div key={deck.id} style={styles.pitchDeckCard}>
                  <div style={styles.pitchDeckTitle}>{deck.title}</div>
                  <div style={styles.pitchDeckFilename}>📄 {deck.filename}</div>
                  <div style={styles.pitchDeckDesc}>{deck.description}</div>
                  <div style={styles.pitchDeckMeta}>
                    Version {deck.version} • {new Date(deck.uploaded_at).toLocaleDateString()}
                  </div>
                  <button
                    style={styles.downloadButton}
                    onClick={() => handleDownloadPitchDeck(deck.id, deck.filename)}
                  >
                    DOWNLOAD
                  </button>
                </div>
              ))}
              {pitchDecks.length === 0 && (
                <div style={styles.empty}>No pitch decks uploaded</div>
              )}
            </div>
          </div>
        )}

        {/* ANALYTICS */}
        {activeTab === 'analytics' && analytics && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Performance Analytics</h2>
            
            <div style={styles.analyticsGrid}>
              <div style={styles.analyticsCard}>
                <h3 style={styles.analyticsCardTitle}>Portfolio Performance</h3>
                <div style={styles.analyticsRow}>
                  <span>Total Invested:</span>
                  <span style={styles.analyticsBold}>{formatCurrency(analytics.portfolio_performance.total_invested)}</span>
                </div>
                <div style={styles.analyticsRow}>
                  <span>Current Value:</span>
                  <span style={styles.analyticsBold}>{formatCurrency(analytics.portfolio_performance.total_current_value)}</span>
                </div>
                <div style={styles.analyticsRow}>
                  <span>Total ROI:</span>
                  <span style={{
                    ...styles.analyticsBold,
                    color: analytics.portfolio_performance.total_roi >= 0 ? '#00C853' : '#ff4444'
                  }}>
                    {analytics.portfolio_performance.total_roi > 0 ? '+' : ''}{analytics.portfolio_performance.total_roi.toFixed(2)}%
                  </span>
                </div>
                <div style={styles.analyticsRow}>
                  <span>Active Investments:</span>
                  <span style={styles.analyticsBold}>{analytics.portfolio_performance.active_investments}</span>
                </div>
              </div>

              <div style={styles.analyticsCard}>
                <h3 style={styles.analyticsCardTitle}>Deal Conversion</h3>
                <div style={styles.analyticsRow}>
                  <span>Closed Won:</span>
                  <span style={{...styles.analyticsBold, color: '#00C853'}}>{analytics.deal_conversion.closed_won}</span>
                </div>
                <div style={styles.analyticsRow}>
                  <span>Closed Lost:</span>
                  <span style={{...styles.analyticsBold, color: '#ff4444'}}>{analytics.deal_conversion.closed_lost}</span>
                </div>
                <div style={styles.analyticsRow}>
                  <span>Active Deals:</span>
                  <span style={styles.analyticsBold}>{analytics.deal_conversion.active_deals}</span>
                </div>
                <div style={styles.analyticsRow}>
                  <span>Win Rate:</span>
                  <span style={styles.analyticsBold}>{analytics.deal_conversion.win_rate.toFixed(2)}%</span>
                </div>
              </div>
            </div>

            {analytics.top_investments.length > 0 && (
              <div style={styles.topInvestments}>
                <h3 style={styles.topInvestmentsTitle}>Top Performing Investments</h3>
                {analytics.top_investments.map((inv, idx) => (
                  <div key={inv.id} style={styles.topInvestmentRow}>
                    <span style={styles.topInvestmentRank}>#{idx + 1}</span>
                    <span style={styles.topInvestmentName}>{inv.investment_name}</span>
                    <span style={{...styles.topInvestmentRoi, color: '#00C853'}}>
                      +{inv.roi_percentage.toFixed(2)}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Styles
const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    height: '100%',
    background: 'black',
    color: '#FFD700',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '30px 40px',
    borderBottom: '1px solid rgba(255, 215, 0, 0.3)',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    opacity: 0.7,
  },
  backButton: {
    padding: '12px 24px',
    background: 'rgba(255, 215, 0, 0.1)',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  error: {
    padding: '15px 40px',
    background: 'rgba(255, 0, 0, 0.1)',
    border: '1px solid #ff4444',
    color: '#ff4444',
  },
  tabBar: {
    display: 'flex',
    gap: '10px',
    padding: '20px 40px',
    borderBottom: '1px solid rgba(255, 215, 0, 0.2)',
    overflowX: 'auto',
  },
  tab: {
    padding: '10px 20px',
    background: 'transparent',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    color: '#FFD700',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },
  tabActive: {
    background: 'rgba(255, 215, 0, 0.2)',
    borderColor: '#FFD700',
  },
  content: {
    flex: 1,
    padding: '40px',
    overflowY: 'auto',
  },
  dashboardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
  },
  card: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '30px',
    textAlign: 'center',
    transition: 'all 0.3s',
    cursor: 'pointer',
  },
  cardIcon: {
    fontSize: '48px',
    marginBottom: '15px',
  },
  cardNumber: {
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  cardLabel: {
    fontSize: '14px',
    opacity: 0.7,
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  cardMeta: {
    fontSize: '12px',
    opacity: 0.6,
    marginTop: '8px',
  },
  section: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
  },
  createButton: {
    padding: '12px 24px',
    background: '#FFD700',
    color: 'black',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  form: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '30px',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  input: {
    padding: '12px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    outline: 'none',
    fontSize: '14px',
  },
  textarea: {
    padding: '12px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    outline: 'none',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  submitButton: {
    padding: '14px',
    background: '#FFD700',
    color: 'black',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  requestsList: {
    display: 'grid',
    gap: '20px',
  },
  requestCard: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '20px',
  },
  requestHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  requestTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
  },
  requestStatus: {
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  requestDesc: {
    fontSize: '14px',
    opacity: 0.8,
    marginBottom: '10px',
  },
  requestAmount: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  requestPurpose: {
    fontSize: '14px',
    opacity: 0.7,
    marginBottom: '8px',
  },
  requestDate: {
    fontSize: '12px',
    opacity: 0.6,
  },
  requestNotes: {
    fontSize: '12px',
    opacity: 0.7,
    marginTop: '10px',
    fontStyle: 'italic',
  },
  portfolioGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
  },
  portfolioCard: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '20px',
  },
  portfolioName: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '12px',
  },
  portfolioAmount: {
    fontSize: '14px',
    marginBottom: '6px',
  },
  portfolioValue: {
    fontSize: '14px',
    marginBottom: '6px',
  },
  portfolioRoi: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  portfolioStatus: {
    fontSize: '12px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  portfolioDate: {
    fontSize: '12px',
    opacity: 0.6,
  },
  dealsList: {
    display: 'grid',
    gap: '15px',
  },
  dealCard: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '20px',
  },
  dealHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  dealName: {
    fontSize: '18px',
    fontWeight: 'bold',
  },
  dealAmount: {
    fontSize: '16px',
    fontWeight: 'bold',
  },
  dealCompany: {
    fontSize: '14px',
    marginBottom: '8px',
  },
  dealStage: {
    fontSize: '14px',
    marginBottom: '6px',
  },
  dealProbability: {
    fontSize: '14px',
    marginBottom: '6px',
  },
  dealDate: {
    fontSize: '12px',
    opacity: 0.6,
  },
  pitchDecksList: {
    display: 'grid',
    gap: '20px',
  },
  pitchDeckCard: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '20px',
  },
  pitchDeckTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  pitchDeckFilename: {
    fontSize: '14px',
    marginBottom: '10px',
  },
  pitchDeckDesc: {
    fontSize: '14px',
    opacity: 0.8,
    marginBottom: '10px',
  },
  pitchDeckMeta: {
    fontSize: '12px',
    opacity: 0.6,
    marginBottom: '15px',
  },
  downloadButton: {
    padding: '10px 20px',
    background: '#FFD700',
    color: 'black',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  analyticsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  analyticsCard: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '20px',
  },
  analyticsCardTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '20px',
  },
  analyticsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px',
    fontSize: '14px',
  },
  analyticsBold: {
    fontWeight: 'bold',
  },
  topInvestments: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '20px',
  },
  topInvestmentsTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '20px',
  },
  topInvestmentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '12px',
    padding: '10px',
    background: 'rgba(255, 215, 0, 0.03)',
    borderRadius: '4px',
  },
  topInvestmentRank: {
    fontSize: '16px',
    fontWeight: 'bold',
    minWidth: '40px',
  },
  topInvestmentName: {
    fontSize: '14px',
    flex: 1,
  },
  topInvestmentRoi: {
    fontSize: '16px',
    fontWeight: 'bold',
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    opacity: 0.7,
    fontSize: '16px',
  },
};
